import { useState } from 'react';
import { Upload, Download, FileJson, Check, AlertCircle, Box, Sparkles, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { adminImportJson, adminExportData, adminExportProtocols, adminExportCrossSell, adminExportScoringRules } from '@/lib/api';
import { toast } from 'sonner';

const downloadFile = (data, filename, format = 'json') => {
  let blob;
  if (format === 'csv') {
    blob = new Blob([data], { type: 'text/csv' });
  } else {
    blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  }
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const jsonToCsv = (items) => {
  if (!items || items.length === 0) return '';
  const headers = Object.keys(items[0]);
  const rows = items.map(item =>
    headers.map(h => {
      const val = item[h];
      if (val === null || val === undefined) return '';
      const str = typeof val === 'object' ? JSON.stringify(val) : String(val);
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    }).join(',')
  );
  return [headers.join(','), ...rows].join('\n');
};

const AdminImport = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exportingKey, setExportingKey] = useState(null);

  const handleImport = async () => {
    if (!jsonInput.trim()) {
      toast.error('Please enter JSON data');
      return;
    }
    try {
      const data = JSON.parse(jsonInput);
      setImporting(true);
      setImportResult(null);
      const result = await adminImportJson(data);
      setImportResult(result.imported);
      toast.success('Data imported successfully');
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON format');
      } else {
        toast.error('Import failed: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async (type, format = 'json') => {
    const key = `${type}-${format}`;
    setExportingKey(key);
    try {
      const dateStr = new Date().toISOString().split('T')[0];
      let data;
      let filename;

      if (type === 'all') {
        data = await adminExportData();
        filename = `supplement-finder-full-export-${dateStr}`;
      } else if (type === 'protocols') {
        data = await adminExportProtocols();
        filename = `protocols-export-${dateStr}`;
      } else if (type === 'cross-sell') {
        data = await adminExportCrossSell();
        filename = `cross-sell-export-${dateStr}`;
      } else if (type === 'scoring-rules') {
        data = await adminExportScoringRules();
        filename = `scoring-rules-export-${dateStr}`;
      }

      if (format === 'csv') {
        const items = data[Object.keys(data)[0]] || [];
        const csv = jsonToCsv(items);
        downloadFile(csv, `${filename}.csv`, 'csv');
      } else {
        downloadFile(data, `${filename}.json`);
      }
      toast.success(`${type} exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(`Export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setExportingKey(null);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setJsonInput(e.target.result);
      toast.success('File loaded');
    };
    reader.onerror = () => toast.error('Failed to read file');
    reader.readAsText(file);
  };

  const exportOptions = [
    { type: 'protocols', label: 'Protocols', desc: 'Protocols with product assignments', icon: Box, color: 'bg-orange-50 text-orange-600' },
    { type: 'cross-sell', label: 'Cross-Sell', desc: 'Cross-sell product assignments', icon: Sparkles, color: 'bg-purple-50 text-purple-600' },
    { type: 'scoring-rules', label: 'Scoring Rules', desc: 'Quiz scoring rules with labels', icon: Settings, color: 'bg-blue-50 text-blue-600' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-2xl text-[#2C2C2C] mb-1">Import / Export</h1>
          <p className="text-stone-600 text-sm">Backup and restore your configuration data</p>
        </div>
        <Button
          onClick={() => handleExport('all')}
          variant="outline"
          className="btn-outline"
          disabled={exportingKey === 'all-json'}
          data-testid="export-all-btn"
        >
          <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
          {exportingKey === 'all-json' ? 'Exporting...' : 'Export All Data (JSON)'}
        </Button>
      </div>

      {/* Granular Exports */}
      <div>
        <h2 className="font-serif text-lg text-[#2C2C2C] mb-3">Quick Exports</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {exportOptions.map((opt) => (
            <Card key={opt.type} data-testid={`export-card-${opt.type}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg ${opt.color} flex items-center justify-center`}>
                    <opt.icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-medium text-stone-800 text-sm">{opt.label}</p>
                    <p className="text-xs text-stone-500">{opt.desc}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleExport(opt.type, 'json')}
                    disabled={exportingKey === `${opt.type}-json`}
                    className="flex-1 text-xs"
                    data-testid={`export-${opt.type}-json`}
                  >
                    <FileJson className="w-3 h-3 mr-1" />
                    {exportingKey === `${opt.type}-json` ? '...' : 'JSON'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleExport(opt.type, 'csv')}
                    disabled={exportingKey === `${opt.type}-csv`}
                    className="flex-1 text-xs"
                    data-testid={`export-${opt.type}-csv`}
                  >
                    <Download className="w-3 h-3 mr-1" />
                    {exportingKey === `${opt.type}-csv` ? '...' : 'CSV'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Import Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-serif flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
              Import JSON Data
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="form-label">Upload JSON File</label>
              <input
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                className="block w-full text-sm text-stone-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-[#4A6763] file:text-white hover:file:bg-[#3A524E] cursor-pointer"
                data-testid="file-upload-input"
              />
            </div>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-stone-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-stone-400">or paste JSON</span>
              </div>
            </div>
            <Textarea
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder='{"systems": [...], "conditions": [...], ...}'
              rows={10}
              className="font-mono text-xs"
              data-testid="json-input"
            />
            <Button
              onClick={handleImport}
              className="w-full btn-primary"
              disabled={importing || !jsonInput.trim()}
              data-testid="import-btn"
            >
              {importing ? 'Importing...' : 'Import Data'}
            </Button>
            {importResult && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-green-700 mb-2">
                  <Check className="w-4 h-4" strokeWidth={1.5} />
                  <span className="font-medium">Import Successful</span>
                </div>
                <div className="text-sm text-green-600 space-y-1">
                  {Object.entries(importResult).map(([key, count]) => (
                    count > 0 && <p key={key}>{key}: {count} records</p>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-serif flex items-center gap-2">
              <FileJson className="w-5 h-5 text-[#C7A07A]" strokeWidth={1.5} />
              About Exports
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3 text-sm text-stone-600">
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-medium text-stone-800 mb-1">Full Export (JSON)</p>
                <p className="text-xs">Contains ALL data: systems, conditions, products, protocols, protocol products, quiz questions, answers, scoring rules, and templates. Use this for complete backups.</p>
              </div>
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-medium text-stone-800 mb-1">Protocols Export</p>
                <p className="text-xs">Each protocol with its assigned products, roles, and condition name. Great for reviewing and documenting your protocol configurations.</p>
              </div>
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-medium text-stone-800 mb-1">Cross-Sell Export</p>
                <p className="text-xs">All cross-sell product assignments with condition names and product names. CSV format is spreadsheet-ready.</p>
              </div>
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-medium text-stone-800 mb-1">Scoring Rules Export</p>
                <p className="text-xs">All answer scoring rules with human-readable question text, answer labels, and target condition/system names.</p>
              </div>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-2 text-amber-700 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-medium">Tip</p>
                  <p className="text-xs mt-1">
                    Schedule regular exports (e.g. weekly) to keep backups of your configuration. 
                    The Full Export JSON can be re-imported to restore your complete setup.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminImport;
